import { yupResolver } from '@hookform/resolvers/yup'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Grid,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { subYears } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { useEffect, useMemo, useState, useCallback, FC } from 'react'
import {
  Controller,
  FieldErrors,
  useForm,
  UseFormRegister,
} from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from 'urql'

import CountriesSelector from '@app/components/CountriesSelector'
import useWorldCountries, {
  WorldCountriesCodes,
} from '@app/components/CountriesSelector/hooks/useWorldCountries'
import { JobTitleSelector } from '@app/components/JobTitleSelector'
import { OrgSelector as ANZOrgSelector } from '@app/components/OrgSelector/ANZ'
import {
  CallbackOption as ANZCallbackOption,
  isHubOrg as ANZIsHubOrg,
  useOrganizationToBeCreatedOnRegistration as ANZUseOrganizationToBeCreatedOnRegistration,
} from '@app/components/OrgSelector/ANZ/utils'
import { OrgSelector as UKOrgSelector } from '@app/components/OrgSelector/UK'
import {
  CallbackOption as UKCallbackOption,
  isHubOrg as UKIsHubOrg,
  useOrganizationToBeCreatedOnRegistration as UKUseOrganizationToBeCreatedOnRegistration,
} from '@app/components/OrgSelector/UK/utils'
import PhoneNumberInput, {
  DEFAULT_PHONE_COUNTRY_ANZ,
  DEFAULT_PHONE_COUNTRY_UK,
} from '@app/components/PhoneNumberInput'
import { Recaptcha, RecaptchaActions } from '@app/components/Recaptcha'
import { useAuth } from '@app/context/auth'
import {
  CompleteProfileMutation,
  CompleteProfileMutationVariables,
  Org_Created_From_Enum,
} from '@app/generated/graphql'
import { useInsertNewOrganization } from '@app/hooks/useInsertNewOrganisationLead'
import { AppLayoutMinimal } from '@app/layouts/AppLayoutMinimal'
import { useResolveSSOProfile } from '@app/modules/sso/hooks/useResolveSSOProfile'
import useRoles from '@app/modules/profile/hooks/useRoles'
import { COMPLETE_PROFILE_MUTATION } from '@app/modules/sso/queries/complete-profile'
import {
  CompleteProfileFormInputs as FormInputs,
  getCompleteProfileFormSchema,
} from '@app/modules/sso/utils'
import { INPUT_DATE_FORMAT, isFullUrl } from '@app/util'
import { Auth } from 'aws-amplify'

type LocationState = {
  redirectTo?: string
}

const ukOrgSelector = (
  register: UseFormRegister<FormInputs>,
  values: FormInputs,
  errors: FieldErrors<FormInputs>,
  isSearchOnlyByPostCodeEnabled: boolean,
  t: (key: string) => string,
  orgSelectorOnChangeUK: (org: UKCallbackOption) => void,
  isUKCountry: (code: string) => boolean,
) => {
  return (
    <UKOrgSelector
      required
      {...register('organization')}
      allowAdding={!isUKCountry(values.countryCode)}
      autocompleteMode={false}
      showTrainerOrgOnly={false}
      value={values.organization ?? undefined}
      error={errors.organization?.message}
      countryCode={values.countryCode}
      textFieldProps={{ variant: 'filled' }}
      onChange={orgSelectorOnChangeUK}
      sx={{ mb: 3 }}
      searchOnlyByPostCode={isSearchOnlyByPostCodeEnabled}
      isShallowRetrieval
      placeholder={
        isSearchOnlyByPostCodeEnabled
          ? undefined
          : t('components.org-selector.post-code-and-name-placeholder')
      }
      label={
        isSearchOnlyByPostCodeEnabled
          ? undefined
          : t('components.org-selector.residing-org')
      }
      showDfeResults={isUKCountry(values.countryCode)}
      createdFrom={Org_Created_From_Enum.RegisterPage}
    />
  )
}

const anzOrgSelector = (
  register: UseFormRegister<FormInputs>,
  values: FormInputs,
  errors: FieldErrors<FormInputs>,
  isSearchOnlyByPostCodeEnabled: boolean,
  t: (key: string) => string,
  orgSelectorOnChangeANZ: (org: ANZCallbackOption) => void,
) => {
  return (
    <ANZOrgSelector
      required
      {...register('organization')}
      allowAdding={false}
      showTrainerOrgOnly={false}
      autocompleteMode={false}
      value={
        values.organization?.id
          ? { id: values.organization.id, name: values.organization.name }
          : undefined
      }
      error={errors.organization?.message}
      countryCode={values.countryCode}
      onChange={orgSelectorOnChangeANZ}
      textFieldProps={{ variant: 'filled' }}
      isShallowRetrieval
      sx={{ mb: 3 }}
      canSearchByAddress={false}
      searchOnlyByPostCode={isSearchOnlyByPostCodeEnabled}
      placeholder={
        isSearchOnlyByPostCodeEnabled
          ? undefined
          : t('components.org-selector.post-code-and-name-placeholder-anz')
      }
      label={
        isSearchOnlyByPostCodeEnabled
          ? undefined
          : t('components.org-selector.residing-org')
      }
      createdFrom={Org_Created_From_Enum.RegisterPage}
      showDfeResults={false}
    />
  )
}

/**
 * Shown after a Cognito Hosted UI (SSO) redirect for a profile that's new or
 * incomplete - collects the business fields Cognito doesn't know about
 * (org, phone, DOB, job title). Works for both brand-new SSO identities and
 * existing, incomplete Connect profiles.
 */
export const CompleteProfilePage: FC = () => {
  const isSearchOnlyByPostCodeEnabled = useFeatureFlagEnabled(
    'search-only-by-postcode-on-registration',
  )
  const {
    acl: { isUK },
    reloadCurrentProfile,
  } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as LocationState | null
  const redirectTo = locationState?.redirectTo

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [isManualFormError, setIsManualFormError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const organizationData = isUK()
    ? UKUseOrganizationToBeCreatedOnRegistration()
    : ANZUseOrganizationToBeCreatedOnRegistration()
  const { getLabel: getCountryLabel, isUKCountry } = useWorldCountries()

  const schema = useMemo(() => getCompleteProfileFormSchema(t), [t])
  const minimalAge = subYears(new Date(), 16)

  const url = import.meta.env.VITE_BASE_WORDPRESS_API_URL
  const { origin } = useMemo(() => (url ? new URL(url) : { origin: '' }), [url])

  const [, insertOrganisation] = useInsertNewOrganization()
  const [, completeProfile] = useMutation<
    CompleteProfileMutation,
    CompleteProfileMutationVariables
  >(COMPLETE_PROFILE_MUTATION)
  const { roles } = useRoles()
  const userRoleId = roles?.find(role => role.name === 'user')?.id

  const {
    cognitoIdentity,
    connectProfile,
    mergedFields,
    fetching: fetchingProfile,
  } = useResolveSSOProfile()

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    reset,
  } = useForm<FormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      familyName: '',
      country: getCountryLabel(isUK() ? 'GB-ENG' : 'AU'),
      countryCode: isUK() ? 'GB-ENG' : 'AU',
      dob: undefined,
      phone: '',
      phoneCountryCode: isUK()
        ? DEFAULT_PHONE_COUNTRY_UK
        : DEFAULT_PHONE_COUNTRY_ANZ,
      recaptchaToken: '',
      jobTitle: '',
      organization: undefined,
    },
  })

  // Backfill the form once any existing Connect/SSO profile data has
  // resolved, so the user only has to review/confirm known fields.
  useEffect(() => {
    if (fetchingProfile) return

    reset({
      firstName: mergedFields.firstName ?? '',
      familyName: mergedFields.familyName ?? '',
      country:
        mergedFields.country ?? getCountryLabel(isUK() ? 'GB-ENG' : 'AU'),
      countryCode: mergedFields.countryCode ?? (isUK() ? 'GB-ENG' : 'AU'),
      dob: mergedFields.dob,
      phone: mergedFields.phone ?? '',
      phoneCountryCode: isUK()
        ? DEFAULT_PHONE_COUNTRY_UK
        : DEFAULT_PHONE_COUNTRY_ANZ,
      recaptchaToken: '',
      jobTitle: mergedFields.jobTitle ?? '',
      organization: mergedFields.organization,
    })
  }, [fetchingProfile, mergedFields, reset, getCountryLabel, isUK])

  const values = watch()

  const onSubmit = async (data: FormInputs) => {
    if (!cognitoIdentity || !userRoleId || isManualFormError) {
      return
    }

    setLoading(true)
    setSubmitError(null)

    try {
      let organizationId = data.organization?.id

      if (organizationData) {
        const { data: newOrganizationData } = await insertOrganisation(
          organizationData,
        )
        organizationId = newOrganizationData?.org?.id
      }

      const input: CompleteProfileMutationVariables['input'] = {
        id: connectProfile?.id ?? cognitoIdentity.id,
        givenName: data.firstName,
        familyName: data.familyName,
        email: cognitoIdentity.email,
        country: data.country,
        countryCode: data.countryCode,
        phone: data.phone,
        phoneCountryCode: data.phoneCountryCode,
        dob: data.dob ? zonedTimeToUtc(data.dob, 'GMT') : null,
        orgId: organizationId,
        jobTitle:
          data.jobTitle === 'Other' ? data.otherJobTitle : data.jobTitle,
        acceptTnc: data.tcs,
        ssoCognitoUserId: cognitoIdentity.id,
        recaptchaToken: data.recaptchaToken,
      }

      const result = await completeProfile({ input })
      if (result.error) {
        throw result.error
      }

      const currentUser = await Auth.currentUserPoolUser()
      await currentUser.refreshSessionIfPossible()
      await reloadCurrentProfile()

      if (redirectTo && isFullUrl(redirectTo)) {
        window.location.href = redirectTo
        return
      }

      navigate(redirectTo || '/', { replace: true })
    } catch (error) {
      console.error('onSubmit: error completing profile', error)
      setSubmitError(t('pages.complete-profile.submit-error'))
      setLoading(false)
    }
  }

  const orgSelectorOnChangeUK = useCallback(
    (org: UKCallbackOption) => {
      if (!org) {
        setValue('organization', undefined, { shouldValidate: true })
        return
      }
      if (UKIsHubOrg(org)) {
        setValue('organization', org, { shouldValidate: true })
      }
    },
    [setValue],
  )

  const orgSelectorOnChangeANZ = useCallback(
    (org: ANZCallbackOption) => {
      if (!org) {
        setValue('organization', undefined, { shouldValidate: true })
        return
      }
      if (ANZIsHubOrg(org)) {
        setValue('organization', org, { shouldValidate: true })
      }
    },
    [setValue],
  )

  if (fetchingProfile) {
    return (
      <Box
        sx={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    )
  }

  return (
    <AppLayoutMinimal width={628}>
      <Typography
        variant="h3"
        fontWeight="600"
        color="secondary"
        mb={1}
        align={isMobile ? 'center' : 'left'}
        textAlign="center"
      >
        {t('pages.complete-profile.heading')}
      </Typography>
      <Typography variant="body1" color="grey.700" mb={3} textAlign="center">
        {t('pages.complete-profile.subheading')}
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        autoComplete="off"
        aria-autocomplete="none"
        data-testid="complete-profile-form"
      >
        <Grid container spacing={3} mb={3}>
          <Grid item md={6} xs={12}>
            <TextField
              id="firstName"
              label={t('first-name')}
              variant="filled"
              placeholder={t('first-name-placeholder')}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              {...register('firstName')}
              inputProps={{ 'data-testid': 'input-first-name' }}
              sx={{ bgcolor: 'grey.100' }}
              fullWidth
              required
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              id="familyName"
              label={t('surname')}
              variant="filled"
              placeholder={t('surname-placeholder')}
              error={!!errors.familyName}
              helperText={errors.familyName?.message}
              {...register('familyName')}
              inputProps={{ 'data-testid': 'input-family-name' }}
              sx={{ bgcolor: 'grey.100' }}
              fullWidth
              required
            />
          </Grid>
        </Grid>

        <Box mb={3}>
          <CountriesSelector
            disableClearable
            onChange={(_, code) => {
              if (code) {
                setValue(
                  'country',
                  getCountryLabel(code as WorldCountriesCodes) ?? '',
                )
                setValue('countryCode', code)
              }
            }}
            value={values.countryCode}
            showAllCountries={isUK() ? undefined : true}
          />
        </Box>

        <Box mb={3}>
          <PhoneNumberInput
            label={t('phone')}
            sx={{ bgcolor: 'grey.100' }}
            variant="filled"
            inputProps={{ sx: { height: 40 }, 'data-testid': 'input-phone' }}
            defaultCountry={
              isUK() ? DEFAULT_PHONE_COUNTRY_UK : DEFAULT_PHONE_COUNTRY_ANZ
            }
            helperText={errors.phone?.message}
            handleManualError={isError => setIsManualFormError(isError)}
            error={!!errors.phone}
            value={{
              phoneNumber: values.phone ?? '',
              countryCode: values.phoneCountryCode,
            }}
            onChange={({ phoneNumber, countryCode }) => {
              setValue('phone', phoneNumber, { shouldValidate: true })
              setValue('phoneCountryCode', countryCode)
            }}
            required
            fullWidth
          />
        </Box>

        <Box mb={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Controller
              control={control}
              name="dob"
              render={({ field }) => (
                <DatePicker
                  label={t('dob')}
                  value={field.value}
                  format={INPUT_DATE_FORMAT}
                  maxDate={minimalAge}
                  onChange={(d: Date | null) => setValue('dob', d)}
                  slotProps={{
                    textField: {
                      variant: 'filled',
                      // @ts-expect-error no arbitrary props are allowed by types, which is wrong
                      'data-testid': 'dob-input',
                      fullWidth: true,
                      sx: { bgcolor: 'grey.100' },
                      error: !!errors.dob,
                      helperText: errors.dob?.message,
                      required: true,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Box>

        {isUK()
          ? ukOrgSelector(
              register,
              values,
              errors,
              Boolean(isSearchOnlyByPostCodeEnabled),
              t,
              orgSelectorOnChangeUK,
              isUKCountry,
            )
          : anzOrgSelector(
              register,
              values,
              errors,
              Boolean(isSearchOnlyByPostCodeEnabled),
              t,
              orgSelectorOnChangeANZ,
            )}

        <Box mb={3}>
          <JobTitleSelector
            errors={{
              jobTitle: errors.jobTitle?.message,
              otherJobTitle: errors.otherJobTitle?.message,
            }}
            register={{
              jobTitle: { ...register('jobTitle') },
              otherJobTitle: { ...register('otherJobTitle') },
            }}
            values={{ jobTitle: values.jobTitle }}
          />
        </Box>

        <Box sx={{ my: 5 }}>
          <FormControlLabel
            control={
              <Checkbox
                {...register('tcs')}
                data-testid="complete-profile-checkbox"
                inputProps={{ 'aria-label': 'T&Cs' }}
              />
            }
            label={
              <>
                <Typography variant="body2">
                  <Trans i18nKey="pages.complete-profile.tcs-label">
                    <a
                      href={`${origin}/policies-procedures/terms-of-use/`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${t('terms-of-use')} (${t(
                        'opens-new-window',
                      )})`}
                    />
                    <a
                      href={`${origin}/policies-procedures/privacy-policy/`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`${t('privacy-policy')} (${t(
                        'opens-new-window',
                      )})`}
                    />
                  </Trans>
                </Typography>
                {errors.tcs ? (
                  <FormHelperText error>{errors.tcs.message}</FormHelperText>
                ) : null}
              </>
            }
          />
        </Box>

        <Box mb={5}>
          <Recaptcha
            action={RecaptchaActions.COMPLETE_PROFILE}
            onSuccess={token =>
              setValue('recaptchaToken', token, { shouldValidate: true })
            }
            onExpired={() =>
              setValue('recaptchaToken', '', { shouldValidate: true })
            }
          />

          {errors.recaptchaToken?.message ? (
            <FormHelperText error>
              {errors.recaptchaToken.message}
            </FormHelperText>
          ) : null}
        </Box>

        <Box display="flex" flexDirection="column" alignItems="center">
          <LoadingButton
            loading={loading}
            type="submit"
            variant="contained"
            color="primary"
            data-testid="complete-profile-submit-btn"
            size="large"
            disabled={!cognitoIdentity || !userRoleId}
          >
            {t('pages.complete-profile.submit-btn')}
          </LoadingButton>

          {submitError ? (
            <FormHelperText
              sx={{ mt: 2 }}
              error
              data-testid="complete-profile-form-error"
            >
              {submitError}
            </FormHelperText>
          ) : null}
        </Box>
      </Box>
    </AppLayoutMinimal>
  )
}
